import { render, html } from 'lit-html';
import attachValidation from '../../utils/form-validation';
import Data from '../../utils/dom/data';
import SelectorEngine from '../../utils/dom/selector-engine';
import Request from '../../utils/request';
import { padNumber, scrollToTop, scrollToNode, getDataFromForm } from '../../utils/misc';
import AddressSearch from '../AddressSearch';

class BankCards {
	constructor(element) {
		this.state = {
			registerAddress: undefined,
			customerRef: undefined,
			stripeElements: []
		};
		this._element = element;
		this._stripe = undefined;
		this._stripeLTA = undefined;
		this._wrapper = undefined;
		this._cardsView = undefined;
		this._newCardView = undefined;
		this._isAmend = false;
		this._mainAddress = undefined;
		this._errorWrapper = undefined;
		this.addressSearch = undefined;
		this._formValidation = undefined;
		this._hasCards = false;
		this._cardNameElement = undefined;

		this._ltaBankCardAdded = undefined;
		this._ltaBankCardRemoved = undefined;

		this.initiateCards();
	}

	async initiateCards() {
		this._wrapper = SelectorEngine.findOne(this._element);

		if (this._wrapper !== null) {
			this._isAmend = SelectorEngine.findOne('.lta-bank-cards--amend');
			this._cardsView = SelectorEngine.findOne('.lta-bank-cards--existing', this._wrapper);
			this._newCardView = SelectorEngine.findOne('.lta-bank-cards--add-card', this._wrapper);
			this._errorWrapper = SelectorEngine.findOne('.lta-bank-cards--error', this._wrapper);

			const addCardCta = SelectorEngine.findOne('.js-cards-add', this._wrapper);
			const cancelWrapper = SelectorEngine.findOne('.lta-bank-cards--add-cancel');
			const cancelAddCardCta = SelectorEngine.findOne('.js-cards-add-cancel', this._wrapper);
			const cardsTitle = SelectorEngine.findOne('.lta-bank-cards--title', this._wrapper);
			const addressRadios = SelectorEngine.find('.js-address-select');
			const mainForm = SelectorEngine.findOne('#lta-bank-cards--form');
			const errorCloseCta = SelectorEngine.findOne('.js-dismiss-cards-error');

			this._ltaBankCardAdded = SelectorEngine.findOne('.lta-bank-card--added', this._wrapper);
			this._ltaBankCardRemoved = SelectorEngine.findOne('.lta-bank-card--removed', this._wrapper);

			this._cardNameElement = SelectorEngine.findOne('#card-name-element');

			const addressSearchEl = SelectorEngine.findOne('.js-bank-cards-address', this._wrapper);
			this.addressSearch = Data.getData(addressSearchEl, 'lta.addresssearch') || new AddressSearch(addressSearchEl);

			cancelAddCardCta.addEventListener('click', () => {
				this.showCards();
			});

			errorCloseCta.addEventListener('click', () => {
				this._errorWrapper.classList.add('u-hidden');
			});

			if (window.S_KEY !== undefined && window.S_KEY_LTA !== undefined) {
				const { loadStripe } = await import('@stripe/stripe-js');
				// Stripe.js implementation
				this._stripe = await loadStripe(window.S_KEY);
				this._stripeLTA = await loadStripe(window.S_KEY_LTA);

				const stripeClasses = {
					base: 'lta-text-input',
					invalid: 'lta-text-input-is-invalid',
					focus: 'lta-text-input-focus'
				};

				const elements = this._stripe.elements();
				const cardNumber = elements.create('cardNumber', {
					classes: stripeClasses
				});
				cardNumber.mount('#card-number-element');

				const cardExpiry = elements.create('cardExpiry', {
					classes: stripeClasses
				});
				cardExpiry.mount('#card-expiry-element');

				const cardCvc = elements.create('cardCvc', {
					classes: stripeClasses
				});
				cardCvc.mount('#card-cvc-element');

				this.state.stripeElements = [cardNumber, cardExpiry, cardCvc];

				this.addFormValidation(mainForm);
			} else {
				// eslint-disable-next-line no-console
				console.error('ERROR: Please check the Stripe Platform and LTA publishable API key.');
				const addCardToggle = SelectorEngine.findOne('.lta-bank-cards--toggle', this._wrapper);
				addCardToggle.classList.add('u-hidden');
			}

			addCardCta.addEventListener('click', () => {
				this.showAddCard();
			});

			addressRadios.forEach((radio) => {
				radio.addEventListener('click', (e) => {
					this.toggleAddress(e);
				});
			});

			const userPayments = await Request.post('/api/payments/customer').catch((error) => {
				console.log(error);
				this.showError('We were unable to retrieve your payment cards	.', false);
			});

			this.handleRequest(userPayments, (response) => {
				const { StripeCustomerRef, CanChangePrimary, DefaultAddress, RegisteredCards } = response;

				this.state.customerRef = StripeCustomerRef;
				this.state.registerAddress = DefaultAddress;
				this.state.canChangePrimary = CanChangePrimary;

				if (RegisteredCards.length > 0) {
					this.replaceCards(RegisteredCards);
					this.showCards();
				} else {
					this.notifyParent();
					if (!this._isAmend) {
						this.showAddCard();
						cancelWrapper.classList.add('u-hidden');
					} else {
						cardsTitle.classList.add('u-hidden');
						this._cardsView.classList.remove('u-hidden');
					}
				}
			});

			const bankCardItems = SelectorEngine.find('.lta-bank-cards--item', this._wrapper);
			bankCardItems.forEach((card) => {
				card.addEventListener('click', (e) => {
					this.selectCard(e);
				});
			});

			const existingCardFormInput = SelectorEngine.findOne('.lta-bank-cards--existing-customerId', this._wrapper);
			if (existingCardFormInput !== null) {
				const customerIdInput = html` <input name="CustomerId" class="u-hidden" value="${this.state.customerRef}" /> `;
				render(customerIdInput, existingCardFormInput);
			}
		}
	}

	handleRequest(response, callback) {
		if (!response) return;
		if (response.Success) {
			callback(response);
		} else {
			this.showError(response.ErrorMessage);
		}
	}

	showAddCard() {
		this.state.stripeElements.forEach((element) => element.clear());
		this._formValidation.resetForm(true);
		this._cardsView.classList.add('u-hidden');
		this._newCardView.classList.remove('u-hidden');
	}

	selectCard(e) {
		const element = e.target;
		const card = SelectorEngine.parents(element, '.lta-bank-cards--item');
		const radioButton = SelectorEngine.find('.lta-form-check-input', card[0])[0];

		if (radioButton) {
			radioButton.checked = true;
		}
	}

	showCards() {
		this._cardsView.classList.remove('u-hidden');
		this._newCardView.classList.add('u-hidden');
		const [firstCta] = SelectorEngine.find('button', this._cardsView);
		scrollToTop();
		if (firstCta) {
			firstCta.focus();
		}
	}

	toggleAddress(e) {
		const radioValue = e.target.value;
		const addressWrapper = SelectorEngine.findOne('.lta-bank-cards--address');
		const isHidden = addressWrapper.classList.contains('u-hidden');
		if (isHidden && radioValue === 'no') {
			addressWrapper.classList.remove('u-hidden');
		}
		if (!isHidden && radioValue === 'yes') {
			addressWrapper.classList.add('u-hidden');
		}
		this._mainAddress = radioValue === 'yes';
		this.state.useRegisteredAddress = this._mainAddress;
	}

	async captureCardPayment(paymentIntentId) {
		return await Request.post('/api/payments/capture', {
			PaymentIntentId: paymentIntentId
		});
	}

	async removeCard(event) {
		const removeCta = event.target;
		const cardId = removeCta.dataset.cardToken;
		const deletedCard = await Request.delete('/api/payments/card', {
			stripeCustomerRef: this.state.customerRef,
			cardToken: cardId
		}).catch(() => {
			this.showError('We were unable to remove the selected card.', false);
		});

		this.handleRequest(deletedCard, (response) => {
			this.replaceCards(response.RegisteredCards);
			this.clearStatus();
			this._ltaBankCardRemoved.classList.toggle('u-hidden');
		});
	}

	clearStatus() {
		if (this._ltaBankCardAdded) {
			this._ltaBankCardAdded.classList.add('u-hidden');
		}
		if (this._ltaBankCardRemoved) {
			this._ltaBankCardRemoved.classList.add('u-hidden');
		}
	}

	async setCardAsPrimary(cardId) {
		if (!this.state.canChangePrimary) {
			this.showError('Sorry, you cannot currently update your primary card as there is a payment processing.');
			return;
		}
		const userCards = await Request.put(`/api/payments/update-primary-card/${cardId}`).catch(() => {
			this.showError("Sorry, we couldn't update your primary payment card, please try again later.");
		});
		this.handleRequest(userCards, (response) => {
			this.replaceCards(response.RegisteredCards);
		});
	}

	createCard(card, i, selected) {
		const templateContainer = SelectorEngine.findOne('.lta-bank-card--template', this._wrapper);
		const cardsWrapper = SelectorEngine.findOne('.lta-bank-cards--existing-content', this._wrapper);
		const [template] = SelectorEngine.children(templateContainer, 'div');
		const cloneTemplate = template.cloneNode(true);
		const cardContent = SelectorEngine.findOne('.lta-location-card--content', cloneTemplate);
		const removeCardCta = SelectorEngine.findOne('.js-cards-remove', cloneTemplate);
		const primaryCta = SelectorEngine.findOne('.js-cards-primary', cloneTemplate);

		if (card.IsPrimary) {
			if (primaryCta !== null) {
				primaryCta.remove();
			}
		} else {
			const primaryTag = SelectorEngine.findOne('.lta-primary-card-tag', cardContent);
			primaryTag.remove();
			if (primaryCta !== null) {
				primaryCta.addEventListener('click', () => {
					this.setCardAsPrimary(card.CardTokenId);
				});
			}
		}

		const cardNumber = document.createTextNode(`**** **** **** ${card.LastFour}`);
		const truncatedYear = `${card.ExpiryYear}`;
		const cardExpiry = document.createTextNode(`Exp: ${padNumber(card.ExpiryMonth)}/${truncatedYear.slice(-2)}`);
		const cardName = document.createTextNode(card.Name);

		const pNumber = document.createElement('p');
		pNumber.append(cardNumber);
		cardContent.append(pNumber);

		const pExpiry = document.createElement('p');
		pExpiry.append(cardExpiry);
		cardContent.append(pExpiry);

		const pName = document.createElement('p');
		pName.append(cardName);
		cardContent.append(pName);

		let cardEvent;

		if (!this._isAmend) {
			const cardId = `card-${i}`;
			const cardInput = SelectorEngine.findOne('input', cloneTemplate);
			cardInput.setAttribute('id', cardId);
			cardInput.setAttribute('value', card.CardTokenId);
			cardInput.checked = card.IsPrimary || selected;
			cardEvent = new CustomEvent('addCard', { bubbles: true, detail: { id: cardId } });
		} else {
			removeCardCta.dataset.cardToken = card.CardTokenId;
			removeCardCta.addEventListener('click', (event) => {
				if (card.IsPrimary || !this.state.canChangePrimary) {
					this.showError('Sorry, the selected card cannot be deleted as it is associated with an active subscription.');
				} else {
					this.removeCard(event);
				}
			});
		}

		cardsWrapper.append(cloneTemplate);
		if (cardEvent instanceof Event) cardsWrapper.dispatchEvent(cardEvent);
	}

	showError(message, recoverable = true) {
		scrollToNode(this._wrapper);
		this._errorWrapper.classList.remove('u-hidden');
		const errorTitle = SelectorEngine.findOne('.title', this._errorWrapper);
		errorTitle.textContent = message;
		const list = SelectorEngine.findOne('ul', this._errorWrapper);
		list.textContent = '';
		const closeCta = SelectorEngine.findOne('button', this._errorWrapper);
		closeCta.focus();
		// TODO: We might need to put this back.
		// if (!recoverable) {
		// 	const li = document.createElement('li');
		// 	const span = document.createElement('span');
		// 	const errorText = document.createTextNode('Please try again in 15 minutes');
		// 	span.append(errorText);
		// 	li.append(span);
		// 	list.append(li);
		// }
	}

	addFormValidation(form) {
		this._formValidation = attachValidation(form, {
			Name: {
				validators: {
					notEmpty: {
						message: 'Please enter a Name'
					}
				}
			},
			isMainAddress: {
				validators: {
					notEmpty: {
						message: 'Please select a billing address'
					}
				}
			}
		});

		this._formValidation.on('core.form.valid', async () => {
			const dataObject = getDataFromForm(form);

			let extraData = {};
			if (this._mainAddress) {
				if (this.state.registerAddress) {
					const { street, city, state, postCode, country } = this.state.registerAddress;
					extraData = {
						name: dataObject.Name,
						address_line1: street,
						address_city: city,
						address_state: state,
						address_zip: postCode,
						address_country: country || 'UK'
					};
				}

				this.addNewCard(extraData);
			} else {
				const addressStatus = await this.addressSearch.validate();
				if (addressStatus === 'Valid') {
					const addressForm = SelectorEngine.findOne('.lta-bank-cards--address-form');
					const addressObject = getDataFromForm(addressForm);

					extraData = {
						name: dataObject.Name,
						address_line1: addressObject.street,
						address_city: addressObject.city,
						address_state: addressObject.state,
						address_zip: addressObject.postCode,
						address_country: addressObject.country || 'UK'
					};

					this.addNewCard(extraData);
				}
			}
		});
	}

	notifyParent() {
		const hasCardEvent = new CustomEvent('cards', {
			bubbles: true,
			detail: { hasCards: this._hasCards }
		});

		this._wrapper.dispatchEvent(hasCardEvent);
	}

	replaceCards(newCards) {
		const cardsWrapper = SelectorEngine.findOne('.lta-bank-cards--existing-content', this._wrapper);
		const existingCards = SelectorEngine.find('.lta-bank-cards--item', cardsWrapper);
		this._hasCards = false;
		if (existingCards.length > 0) {
			existingCards.forEach((card) => card.remove());
		}
		if (newCards.length > 0) {
			this._hasCards = true;
			newCards.forEach((card, i) => {
				this.createCard(card, i, newCards.length === 1);
			});
		}
		this.notifyParent();
		this.clearStatus();
		scrollToTop();
	}

	async addNewCard(extraData) {
		const [stripeElement] = this.state.stripeElements;
		let address = this.addressSearch.state.useRegisteredAddress
			? this.addressSearch.state.address
			: this.state.registerAddress;

		const billingDetails = {
			name: this._cardNameElement.value,
			address: {
				line1: address.street,
				city: address.city,
				country: 'GB',
				postal_code: address.postCode
			}
		};
		const stripeResponse = await this._stripe.createPaymentMethod({
			type: 'card',
			card: stripeElement,
			billing_details: billingDetails
		});

		if (stripeResponse.error) {
			this.showError('There are some errors with your entries.');
		} else {
			const newStripeCard = {
				stripeCustomerRef: this.state.customerRef,
				cardToken: stripeResponse.paymentMethod.id
			};

			const newCard = await Request.post('/api/payments/card', newStripeCard).catch((error) => {
				this.showError('We have had some troubles adding the card.', false);
			});

			if (newCard.Status == 'REQ_AUTHORIZATION') {
				const { setupIntent, error } = await this._stripe
					.confirmCardSetup(newCard.ClientSecret, {
						payment_method: {
							card: stripeElement,
							billing_details: billingDetails
						}
					})
					.catch(() => {
						this.showError('We were unable to add your card.', false);
					});

				if (error) {
					this.showError(error.message, false);
				} else {
					const customer = await Request.post('/api/payments/customer')
						.then((response) => {
							this.replaceCards(response.RegisteredCards);
							this.showCards();
							this._ltaBankCardAdded.classList.toggle('u-hidden');
						})
						.catch((error) => {
							this.showError('We were unable to retrieve your payment cards.', false);
						});
				}
			} else {
				this.handleRequest(newCard, (response) => {
					this.replaceCards(response.RegisteredCards);
					this.showCards();
					this._ltaBankCardAdded.classList.toggle('u-hidden');
				});
			}
		}
	}
}

export default BankCards;
